import { RRule } from 'rrule'
import Select from '../ui/Select'
import Input from '../ui/Input'
import Button from '../ui/Button'
import { buildRRuleString } from '../../lib/recurrence'

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const FREQ_LABELS = { DAILY: 'day', WEEKLY: 'week', MONTHLY: 'month', YEARLY: 'year' }

const pad = n => String(n).padStart(2, '0')
const toDateValue = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

function previewText(value, dtstart) {
    try {
        const str = buildRRuleString(value)
        const parsed = RRule.parseString(str)
        const rule = new RRule({ ...parsed, dtstart })
        return rule.toText()
    } catch {
        return ''
    }
}

export default function RecurrenceEditor({ value, onChange, dtstart, readOnly = false }) {
    const set = patch => onChange({ ...value, ...patch })

    const toggleWeekday = (n) => {
        const has = value.byweekday.includes(n)
        set({ byweekday: has ? value.byweekday.filter(d => d !== n) : [...value.byweekday, n].sort() })
    }

    const handleUntilChange = (dateStr) => {
        if (!dateStr) return
        const [y, m, d] = dateStr.split('-').map(Number)
        set({ until: new Date(y, m - 1, d, dtstart.getHours(), dtstart.getMinutes(), dtstart.getSeconds()) })
    }

    if (readOnly) {
        return (
            <p className="font-mono text-[11px] text-zinc-500">
                Repeats {previewText(value, dtstart) || '—'} (pattern carried over from the series; edit via "All events" to change it).
            </p>
        )
    }

    return (
        <div className="space-y-2 border-2 border-black dark:border-white p-3">
            <div className="flex items-center gap-2">
                <span className="font-mono text-xs uppercase text-zinc-500">Every</span>
                <Input
                    type="number"
                    min={1}
                    value={value.interval}
                    onChange={e => set({ interval: Math.max(1, Number(e.target.value) || 1) })}
                    className="w-16"
                />
                <Select value={value.freq} onChange={e => set({ freq: e.target.value })} className="w-auto">
                    {Object.entries(FREQ_LABELS).map(([freq, label]) => (
                        <option key={freq} value={freq}>{label}{value.interval > 1 ? 's' : ''}</option>
                    ))}
                </Select>
            </div>

            {value.freq === 'WEEKLY' && (
                <div className="flex flex-wrap gap-1.5">
                    {WEEKDAY_LABELS.map((label, n) => (
                        <button
                            key={label}
                            type="button"
                            onClick={() => toggleWeekday(n)}
                            className={[
                                'h-8 w-10 border-2 font-mono text-xs uppercase transition-colors',
                                value.byweekday.includes(n)
                                    ? 'bg-accent border-black dark:border-white text-black'
                                    : 'bg-white dark:bg-zinc-950 border-black dark:border-white text-black dark:text-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black',
                            ].join(' ')}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs uppercase text-zinc-500">Ends</span>
                <Button type="button" variant={value.endMode === 'never' ? 'primary' : 'ghost'} onClick={() => set({ endMode: 'never' })} className="!px-2 !py-1 text-xs">
                    Never
                </Button>
                <Button type="button" variant={value.endMode === 'until' ? 'primary' : 'ghost'} onClick={() => set({ endMode: 'until' })} className="!px-2 !py-1 text-xs">
                    On date
                </Button>
                {value.endMode === 'until' && (
                    <Input type="date" value={value.until ? toDateValue(value.until) : ''} onChange={e => handleUntilChange(e.target.value)} className="w-40" />
                )}
                <Button type="button" variant={value.endMode === 'count' ? 'primary' : 'ghost'} onClick={() => set({ endMode: 'count' })} className="!px-2 !py-1 text-xs">
                    After
                </Button>
                {value.endMode === 'count' && (
                    <>
                        <Input
                            type="number"
                            min={1}
                            value={value.count || 1}
                            onChange={e => set({ count: Math.max(1, Number(e.target.value) || 1) })}
                            className="w-16"
                        />
                        <span className="font-mono text-xs text-zinc-500">occurrences</span>
                    </>
                )}
            </div>

            <p className="font-mono text-[11px] text-zinc-500">{previewText(value, dtstart)}</p>
        </div>
    )
}

export const defaultRecurrenceDraft = () => ({
    freq: 'WEEKLY',
    interval: 1,
    byweekday: [],
    endMode: 'never',
    until: null,
    count: null,
})
